import styles from './OrderCardPackage.module.scss';
import {
  WidgetContainer,
  StatusDatePanel,
  ProductImage,
  ExpandedQuantityTab,
  AdditionalInfo,
  Payments,
} from '@chewy/chat-library-components';
import { PackageCardData } from '@chewy/chat-library-common';
import { KibTruncation, KibTruncationContent, KibTruncationToggle } from '@chewy/kib-truncation-react';
import '@chewy/kib-truncation-styles/dist/kib-truncation.css';
import { first } from 'lodash';
import { forwardRef, useCallback, useRef } from 'react';
import { useMaxVisibleItems } from './useMaxVisibleItems';

export interface OrderCardPackageProps {
  data: PackageCardData[];
  isSelectable?: boolean;
  isSelected?: boolean;
  isUnselected?: boolean;
  stackDateStatus?: boolean;
  onSelect?: () => void;
}

const OrderCardPackage = forwardRef<HTMLDivElement, OrderCardPackageProps>(({  data,
  isSelectable = false,
  isSelected,
  isUnselected,
  stackDateStatus = false,
  onSelect,
}: OrderCardPackageProps, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const MAX_VISIBLE_ITEMS = useMaxVisibleItems(containerRef, { initialMax: 4 });
  const packageData = first(data);
  const packageNumber = packageData?.packageNumber ?? '';
  const totalPackages = packageData?.totalPackages ?? 1;
  const orderStatus = packageData?.orderStatus ?? '';
  const dateStatus = packageData?.dateStatus ?? { label: '', status: '', date: '' };
  const items = packageData?.items ?? [];
  const remainingCount = items.length - MAX_VISIBLE_ITEMS;
  // Avoid a "+1" badge; show the final thumbnail in the reserved badge slot instead.
  const showBadge = remainingCount > 1;
  const visibleItems = items.slice(0, showBadge ? MAX_VISIBLE_ITEMS : items.length);
  const additionalMetaData = packageData?.additionalMetaData ?? [];
  const hasAdditionalInfo = additionalMetaData.length > 0;
  const isPackage = packageData?.cardType === 'PACKAGE';
  const payments = packageData?.payments ?? [];
  const hasPayments = payments.length > 0;

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);

  return (
    <WidgetContainer
      isSelectable={isSelectable}
      isSelected={isSelected}
      isUnselected={isUnselected}
      onSelect={onSelect}
      data={data}
    >
      <div ref={setContainerRef} className={styles.packageCard}>
        <StatusDatePanel
          orderStatus={orderStatus}
          dateStatus={dateStatus}
          stackDateStatus={stackDateStatus}
        />

        <KibTruncation>
          <KibTruncationContent
            summary={
              <div className={styles.itemsGrid}>
                {visibleItems.map((item, index) => (
                  <ProductImage
                    key={index}
                    imageUrl={item.imageUrl || ''}
                    name={item.name || `${isPackage ? 'Package' : 'Return'} item`}
                    quantity={item.quantity}
                  />
                ))}
                {showBadge && (
                  <ExpandedQuantityTab remainingCount={remainingCount} />
                )}
              </div>
            }
          >
            <div className={`${styles.itemsGrid} ${styles.expanded}`}>
              {items.map((item, index) => (
                <ProductImage
                  key={index}
                  imageUrl={item.imageUrl || ''}
                  name={item.name || `${isPackage ? 'Package' : 'Return'} item`}
                  quantity={item.quantity}
                />
              ))}
            </div>
          </KibTruncationContent>
          {showBadge && <KibTruncationToggle className={styles.truncationToggle} labelMore="Show all" labelLess="Show less" />}
        </KibTruncation>
        <div className={styles.packageInfoContent}>
          {hasAdditionalInfo && (
            <div>
              <AdditionalInfo additionalInfo={additionalMetaData} />
            </div>
          )}

          {hasPayments && (
            <div>
              <Payments payments={payments} />
            </div>
          )}
        </div>

        {totalPackages > 1 && (<div className={styles.packageLabel}>
          <span className={styles.packageNumber}>{`${packageNumber}/${totalPackages}`}</span>
        </div>
        )}
      </div>
    </WidgetContainer>
  );
});

export default OrderCardPackage;
